// Eartrainer's Untitled1 font encoding. Persist plain text; encode only for display.
const GLYPHS = [
  ['7-8', '\u01a7'], ['9-8', '\u01a8'], ['4-3', '\u01a9'],
  ['75', '\u01ab'], ['65', '\u01a2'], ['64', '\u01a1'],
  ['43', '\u01a3'], ['42', '\u01a4'], ['53', '\u01a6'],
  ['6', '\u01a0'], ['7', '\u01be'], ['8', '\u01aa'],
  ['/o', '\u01c1'], ['o', '\u01bd'], ['b', '\u0231'],
];
export function decodeRomanNumeralText(value) {
  let text = String(value ?? '');
  for (const [plain, glyph] of GLYPHS) text = text.split(glyph).join(plain);
  return text;
}
export function plainRomanNumeralText(value) {
  return decodeRomanNumeralText(value).replace(/\s/g, '')
    .replace(/♭/g, 'b').replace(/♯/g, '#').replace(/[øØ]/g, '/o')
    .replace(/[°º0]/g, 'o').replace(/[‐‑‒–—−]/g, '-');
}
export function encodeRomanNumeralText(value) {
  return plainRomanNumeralText(value).replace(/7-8|9-8|4-3|75|65|64|43|42|53|6|7|8|\/o|o|b/g,
    match => GLYPHS.find(([plain]) => plain === match)[1]);
}
export function normalizeRomanNumeral(value) {
  return plainRomanNumeralText(value)
    // Slashes between figures are formatting; secondary-function slashes remain.
    .replace(/(\d)\/(?=\d)/g, '$1')
    .replace(/([ivIV]+(?:\+|\/o|o)?)(?:53)(?=\/|$)/g, '$1')
    .replace(/([ivIV]+(?:\+|\/o|o)?)(?:63)(?=\/|$)/g, (_, rn) => rn + '6')
    .replace(/([ivIV]+(?:\+|\/o|o)?)(?:2)(?=\/|$)/g, (_, rn) => rn + '42');
}
export function romanNumeralsMatch(expected, actual) {
  const key = normalizeRomanNumeral(expected), got = normalizeRomanNumeral(actual);
  if (!key || !got) return false;
  if (/^(cad|k)64$/i.test(key)) return /^(cad|k|I|V)64$/i.test(got);
  return key === got;
}
export function timestampNumber(value) {
  if (typeof value === 'number') return value;
  return value?.RealValue ?? value?.realValue ??
    ((value?.Numerator ?? value?.numerator ?? 0) / (value?.Denominator ?? value?.denominator ?? 1));
}
export function rnAnchorId(measure, time) {
  return `m${measure}-t${Number(Number(time).toFixed(8))}`;
}
export function matchesRNResolution(time, resolution = 'quarter') {
  if (resolution === 'all') return true;
  if (resolution === 'downbeat') return Math.abs(time) < 1e-7;
  const step = {sixteenth: 1/16, eighth: 1/8, quarter: 1/4, dottedQuarter: 3/8, half: 1/2, whole: 1}[resolution] ?? 1/4;
  return Math.abs(time / step - Math.round(time / step)) < 1e-6;
}
export const romanNumeralTemplate = {
  type: 'roman-numeral-analysis',
  config: { number: '', prompt: 'Analyze the marked chords using Roman numerals and inversions.',
    points: 10, musicXML: '', promptResolution: 'quarter', prompts: [], correct: {} },
};

export const leadSheetChordOptions = ['C', 'F', 'G', 'G7'];
export function leadSheetChordMatches(expected, actual) {
  return leadSheetChordOptions.includes(actual) && expected === actual;
}

export function validateRomanNumeralConfig(config = {}) {
  if (!String(config.musicXML || '').trim()) return 'Upload a MusicXML score.';
  if (!config.prompts?.length) return 'Place at least one Roman numeral prompt.';
  const seen = new Set();
  for (const prompt of config.prompts) {
    if (!prompt.id || prompt.id !== rnAnchorId(prompt.measure, prompt.time) || seen.has(prompt.id)) return 'RN prompt positions are invalid or duplicated. Recreate prompt placement.';
    seen.add(prompt.id);
    const expected = config.correct?.[prompt.id];
    const alternatives = Array.isArray(expected) ? expected : [expected];
    if (config.answerMode === 'chord-select' && (!alternatives.length || alternatives.some(key => !leadSheetChordOptions.includes(key)))) return `Choose C, F, G, or G7 for measure ${prompt.measure}, position ${prompt.time}.`;
    if (!alternatives.some(key => plainRomanNumeralText(key))) return `Enter the answer key for measure ${prompt.measure}, position ${prompt.time}.`;
  }
  const known = new Map((config.prompts || []).map((p,i)=>[p.id,i]));
  const cadenceIds = new Set();
  for (const q of config.cadenceQuestions || []) {
    if (!q.id || cadenceIds.has(q.id)) return 'Cadence labels must have unique IDs.';
    cadenceIds.add(q.id);
    if (!known.has(q.fromPromptId) || !known.has(q.toPromptId) || known.get(q.fromPromptId)>known.get(q.toPromptId)) return `Choose valid first and last chords for ${q.id}.`;
    const answers=Array.isArray(q.correct)?q.correct:[q.correct];
    if(!answers.some(s=>String(s || '').trim())) return `Enter the answer key for ${q.id} cadence.`;
  }
  if(config.keyIdentification && (!/^[A-Ga-g](?:[#b♯♭]{0,2})$/.test(String(config.keyIdentification.correct?.key || '').trim()) || !['major','minor'].includes(config.keyIdentification.correct?.mode))) return 'Enter a valid tonic and mode for key identification.';
  const extra=(config.keyIdentification?Number(config.keyIdentification.points ?? 1):0)+(config.cadenceQuestions || []).reduce((sum,q)=>sum+Number(q.points ?? 1),0);
  const total=Number(config.points)||0;
  if(extra>total || (config.gradingPoints?.romanNumerals != null && Math.abs(Number(config.gradingPoints.romanNumerals)+extra-total)>1e-7)) return 'Roman numeral, key, and cadence points must add up to the question total.';
  return '';
}
